/**
 * NEXORA AI - Mobile Header Component
 * Strategic shared component - Future main header of the application
 * 
 * Epic 1.3 - Responsive Premium
 * Phase 2.1 - Composants Partagés
 * 
 * Architecture: Extensible with slots for future features
 * - Breadcrumbs (disabled)
 * - Global Search (disabled)
 * - Notifications (disabled)
 * - User Avatar (disabled)
 * - Contextual Actions (disabled)
 * - Sync Indicator (disabled)
 * - Language Selector (disabled)
 * 
 * Specifications:
 * - Height: 64px (h-16) uniform
 * - Z-index: z-50
 * - Touch target hamburger: 44x44px minimum
 * - Title: Never truncated, centered
 * - Background: bg-zinc-900/90 backdrop-blur-xl
 * - Border: border-b border-zinc-800/50
 */

import React from 'react';
import { Menu, X, ChevronRight, Search, Bell, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * MobileHeader Component
 * 
 * @param {Object} props
 * @param {Function} props.onMenuToggle - Required: Callback when hamburger clicked
 * @param {boolean} props.isMenuOpen - Required: Current menu state
 * @param {string|React.Component} props.title - Optional: Page title (default: "NEXORA AI")
 * @param {boolean} props.showBreadcrumb - Optional: Show breadcrumb (default: false)
 * @param {Array<{label: string, path: string}>} props.breadcrumbItems - Optional: Breadcrumb items
 * @param {boolean} props.showSearch - Optional: Show search icon (default: false)
 * @param {Function} props.onSearchClick - Optional: Search callback
 * @param {boolean} props.showNotifications - Optional: Show notification bell (default: false)
 * @param {number} props.notificationCount - Optional: Unread notifications count
 * @param {Function} props.onNotificationsClick - Optional: Notifications callback
 * @param {boolean} props.showAvatar - Optional: Show user avatar (default: false)
 * @param {Object} props.user - Optional: User data {name, email, avatar}
 * @param {Function} props.onAvatarClick - Optional: Avatar click callback
 * @param {React.ReactNode} props.actions - Optional: Custom actions slot (JSX)
 * @param {boolean} props.showSyncIndicator - Optional: Show sync status (default: false)
 * @param {string} props.syncStatus - Optional: 'syncing' | 'synced' | 'error'
 * @param {boolean} props.showLanguageSelector - Optional: Show language selector (default: false)
 * @param {Function} props.onLanguageClick - Optional: Language selector callback
 * @param {string} props.className - Optional: Additional CSS classes
 */
const MobileHeader = ({
  // Required props
  onMenuToggle,
  isMenuOpen,
  
  // Title props
  title = "NEXORA AI",
  
  // Breadcrumb props (future)
  showBreadcrumb = false,
  breadcrumbItems = [],
  
  // Search props (future)
  showSearch = false,
  onSearchClick,
  
  // Notifications props (future)
  showNotifications = false,
  notificationCount = 0,
  onNotificationsClick,
  
  // Avatar props (future)
  showAvatar = false,
  user = null,
  onAvatarClick,
  
  // Custom actions slot (future)
  actions = null,
  
  // Sync indicator props (future)
  showSyncIndicator = false,
  syncStatus = 'synced', // 'syncing' | 'synced' | 'error'
  
  // Language selector props (future)
  showLanguageSelector = false,
  onLanguageClick,
  
  // Additional styling
  className = "",
}) => {
  return (
    <header
      className={`fixed top-0 left-0 right-0 h-16 z-50 bg-zinc-900/90 backdrop-blur-xl border-b border-zinc-800/50 ${className}`}
      data-testid="mobile-header"
    >
      <div className="h-full flex items-center justify-between px-4 gap-3">
        {/* Left Section: Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onMenuToggle}
            className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-800/50 hover:bg-zinc-700 border border-zinc-700/50 text-white transition-colors active:scale-95"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            data-testid="mobile-header-hamburger"
          >
            <motion.div
              initial={false}
              animate={{ rotate: isMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </motion.div>
          </button>

          {/* Breadcrumb (Future - Slot reserved) */}
          {showBreadcrumb && breadcrumbItems.length > 0 && (
            <nav className="hidden sm:flex items-center gap-2 text-sm" aria-label="Breadcrumb">
              {breadcrumbItems.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <ChevronRight size={14} className="text-zinc-600" />}
                  <a
                    href={item.path}
                    className={`${
                      index === breadcrumbItems.length - 1
                        ? 'text-white font-medium'
                        : 'text-zinc-400 hover:text-zinc-200'
                    } transition-colors`}
                  >
                    {item.label}
                  </a>
                </React.Fragment>
              ))}
            </nav>
          )}
        </div>

        {/* Center Section: Title */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          {typeof title === 'string' ? (
            <h1 className="text-base font-bold text-white truncate px-4" data-testid="mobile-header-title">
              <span className="gradient-text">{title}</span>
            </h1>
          ) : (
            title
          )}
        </div>

        {/* Right Section: Actions, Search, Notifications, Avatar, Sync, Language */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Sync Indicator (Future - Slot reserved) */}
          {showSyncIndicator && (
            <div
              className={`w-2 h-2 rounded-full ${
                syncStatus === 'syncing'
                  ? 'bg-blue-500 animate-pulse'
                  : syncStatus === 'synced'
                  ? 'bg-green-500'
                  : 'bg-red-500'
              }`}
              title={`Status: ${syncStatus}`}
              data-testid="mobile-header-sync-indicator"
            />
          )}

          {/* Search Icon (Future - Slot reserved) */}
          {showSearch && (
            <button
              onClick={onSearchClick}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              aria-label="Search"
              data-testid="mobile-header-search"
            >
              <Search size={20} />
            </button>
          )}

          {/* Notifications Icon (Future - Slot reserved) */}
          {showNotifications && (
            <button
              onClick={onNotificationsClick}
              className="relative w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              aria-label={`Notifications ${notificationCount > 0 ? `(${notificationCount} unread)` : ''}`}
              data-testid="mobile-header-notifications"
            >
              <Bell size={20} />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>
          )}

          {/* Language Selector (Future - Slot reserved) */}
          {showLanguageSelector && (
            <button
              onClick={onLanguageClick}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              aria-label="Change language"
              data-testid="mobile-header-language"
            >
              <Globe size={20} />
            </button>
          )}

          {/* User Avatar (Future - Slot reserved) */}
          {showAvatar && user && (
            <button
              onClick={onAvatarClick}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white text-sm font-semibold flex items-center justify-center ring-2 ring-zinc-800 hover:ring-blue-500/50 transition-all"
              aria-label={`User menu: ${user.name || user.email}`}
              data-testid="mobile-header-avatar"
            >
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'
              )}
            </button>
          )}

          {/* Custom Actions Slot (Future - Slot reserved) */}
          {actions && (
            <div className="flex items-center gap-2" data-testid="mobile-header-actions">
              {actions}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
